import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { format } from "date-fns";
import AdminService from "../../../services/admin.service";
import { CashCategory, CashMovement, CashRegisterCheckpoint, DayWorkedRow, HealthSeller } from "../../../types/common.types";
import Sales from "./Sales";
import DaysWorked from "./DaysWorked";
import formatNumberToString from "../../../utils/formatNumber";
import { useGlobal } from "../../../context/global";
import generateWeeklySummaryPDF from "./generateWeeklySummaryPDF";

export default function SellerInfo({id, day, endDay}: {id: string, day: Date, endDay?: Date}) {
    const { setIsLoading, setMessage } = useGlobal()
    const [data, setData] = useState<Partial<HealthSeller>>({sales: []})
    const [shifts, setShifts] = useState<DayWorkedRow[]>([])
    const [checkpoints, setCheckpoints] = useState<CashRegisterCheckpoint[]>([])
    const [advances, setAdvances] = useState<CashMovement[]>([])
    const [sellerName, setSellerName] = useState('')
    const [commissionCategoryId, setCommissionCategoryId] = useState('')

    async function getInfo() {
        setIsLoading(true)
        try {
            if (!day) return setData({} as HealthSeller)
            // allSettled, no all: esta pantalla es de donde sale el pago semanal de sueldo — si UNA
            // sola de las 6 llamadas falla (ej. un timeout transitorio), antes se perdía todo en
            // silencio y la pantalla mostraba "$0 a pagar" sin ningún aviso. Ahora cada pieza se
            // aplica de forma independiente, y si algo falló se avisa en vez de mostrar un total falso.
            const [salesRes, daysRes, checkpointsRes, advancesRes, sellerRes, categoriesRes] = await Promise.allSettled([
                AdminService.helthByDay(id, day, endDay),
                AdminService.getDaysWorked(id, day, endDay || day),
                AdminService.listUnsettledCheckpoints(id, day, endDay || day),
                AdminService.listUnsettledAdvances(id, day, endDay || day),
                AdminService.getSeller(id),
                AdminService.listCashCategories('egreso'),
            ])
            if (salesRes.status === 'fulfilled') setData({ sales: salesRes.value.data.sales })
            if (daysRes.status === 'fulfilled') setShifts(daysRes.value.data.shifts || [])
            if (checkpointsRes.status === 'fulfilled') setCheckpoints(checkpointsRes.value.data)
            if (advancesRes.status === 'fulfilled') setAdvances(advancesRes.value.data)
            if (sellerRes.status === 'fulfilled') setSellerName(sellerRes.value.data.name)
            if (categoriesRes.status === 'fulfilled') {
                const commissionCategory = (categoriesRes.value.data as CashCategory[]).find(c => c.name === 'Sueldo / Comisión Vendedores')
                if (commissionCategory) setCommissionCategoryId(commissionCategory.id)
            }
            const failures = [salesRes, daysRes, checkpointsRes, advancesRes, sellerRes, categoriesRes].filter(r => r.status === 'rejected')
            if (failures.length > 0) {
                console.log(failures)
                setMessage({ type: 'error', content: 'No se pudo cargar toda la información del vendedor — revisa antes de generar un pago' })
                setTimeout(() => setMessage(undefined), 4000)
            }
        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        getInfo()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, day, endDay])

    const desfasesTotal = checkpoints.reduce((acc, c) => acc + Number(c.difference), 0)

    async function settleDesfases() {
        if (!day) return
        try {
            setIsLoading(true)
            await AdminService.settleUnsettledCheckpoints(id, day, endDay || day)
            setCheckpoints([])
        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false)
        }
    }
    const total_sales = data.sales?.map(sale => sale.reservation.price).filter(amount => amount).reduce((a: number,b: number) => a + b, 0) || 0

    const agency_earnings = data.sales?.map(sale => {
        const groupCosts = sale.reservation.trip?.costs?.length > 0 ? sale.reservation.trip?.costs?.map(obj => obj.value).reduce((a, b) => a + b, 0) / sale.reservation.trip?.reservations : 0
        const costs = groupCosts + (sale.reservation.cost || 0)
        const commission = sale.amount || ((sale.reservation.price * sale.seller.sell_commission) / 100)
        return (sale.reservation.traspass_price ? 
        sale.reservation.price - sale.reservation.traspass_price - commission  
        : sale.reservation.price - costs - commission)
    })?.reduce((a,b) => a + b, 0)
    console.log(agency_earnings)

    const pending = useMemo(() => {
        let total = 0
        if (!data.sales) return 0
        for (let i = 0; i < data.sales.length; i++) {
            const sale = data.sales[i];
            if (!sale.authorized) {
                const commission = sale.amount || (sale.reservation_price * (sale.seller.sell_commission / 100))
                total = total + commission
                continue;
            }
        }
        return total
    }, [data])
    const paid = useMemo(() => {
        let total = 0
        if (!data.sales) return 0
        for (let i = 0; i < data.sales.length; i++) {
            const sale = data.sales[i];
            if (sale.authorized) {
                total = total + sale.amount
                continue;
            }
        }
        return total
    }, [data])

    // Derivado en vivo de `shifts`: al editar el monto de un día (antes de guardar), estos números
    // ya reflejan el cambio, igual que "Comisión total" reacciona al editar una venta.
    const daysInfo = useMemo(() => {
        const seller_days = shifts.filter(s => s.role === 'seller').length
        const admin_days = shifts.filter(s => s.role === 'admin').length
        const seller_amount = shifts.filter(s => s.role === 'seller').reduce((acc, s) => acc + Number(s.amount), 0)
        const admin_amount = shifts.filter(s => s.role === 'admin').reduce((acc, s) => acc + Number(s.amount), 0)
        return { days_worked: seller_days + admin_days, seller_days, admin_days, seller_amount, admin_amount, total_amount: seller_amount + admin_amount }
    }, [shifts])

    const advancesTotal = advances.reduce((acc, a) => acc + Number(a.amount), 0)
    const totalAPagar = paid + pending + daysInfo.total_amount + desfasesTotal - advancesTotal

    // Crea el pago pendiente en Caja con el monto neto de este período, y de paso "reserva" las
    // comisiones/días que lo componen (no se pueden volver a incluir en otro pago) y salda los
    // anticipos ya restados — todo en la misma acción. Los abonos se siguen manejando desde Caja.
    async function createWeeklyPendingPayment() {
        if (totalAPagar <= 0) return
        setIsLoading(true)
        try {
            const notes = `Semana ${format(day, 'dd/MM/yyyy')}${endDay ? ' - ' + format(endDay, 'dd/MM/yyyy') : ''}`
            const saleIds = (data.sales || []).filter(s => !s.authorized).map(s => s.id)
            const shiftIds = shifts.filter(s => !s.authorized).map(s => s.id)
            const advanceIds = advances.map(a => a.id)
            await AdminService.createCashPendingPayment({
                category_id: commissionCategoryId,
                counterpart_name: sellerName,
                counterpart_seller_id: id,
                total_amount: totalAPagar,
                notes,
                sale_ids: saleIds,
                shift_ids: shiftIds,
                advance_movement_ids: advanceIds,
            })
            setMessage({ type: 'success', content: 'Pago pendiente creado en Caja' })
            setTimeout(() => setMessage(undefined), 2500)
            await getInfo()
        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false)
        }
    }

    // Genera el PDF de resumen semanal por separado, sin crear (ni depender de) el pago pendiente —
    // David lo usa para mandarle el detalle al vendedor por WhatsApp, se haya cerrado el pago o no.
    function createWeeklySummary() {
        generateWeeklySummaryPDF({
            sellerName,
            day,
            endDay,
            sales: data.sales || [],
            commissionTotal: paid + pending,
            daysWorkedCount: daysInfo.days_worked,
            daysWorkedAmount: daysInfo.total_amount,
            desfasesTotal: desfasesTotal || undefined,
            advancesTotal: advancesTotal || undefined,
            total: totalAPagar
        })
    }

    return (
        <Main>
            <div className="accounting">
                <div>
                    pending 
                    <span>$ {formatNumberToString(pending)}</span>
                </div>
                <div>
                    pagado
                    <span>$ {formatNumberToString(paid)}</span>
                </div>
            </div>
            <div className="accounting">
                <div>
                    Comisión total
                    <span>$ {formatNumberToString(paid + pending)}</span>
                </div>
                <div>
                    Venta total
                    <span>$ {formatNumberToString(total_sales)}</span>
                </div>
                <div>
                    Ganancia agencia
                    <span>$ {formatNumberToString(agency_earnings || 0)}</span>
                </div>
                <div>
                    Días trabajados
                    <span>{daysInfo.days_worked} ({daysInfo.seller_days} vendedor / {daysInfo.admin_days} admin)</span>
                </div>
                <div>
                    Total días
                    <span>$ {formatNumberToString(daysInfo.total_amount)}</span>
                </div>
                <div>
                    Total días y comisión
                    <span>$ {formatNumberToString(daysInfo.total_amount + paid + pending)}</span>
                </div>
                {checkpoints.length > 0 && (
                    <div>
                        Desfases (caja)
                        <span className={desfasesTotal < 0 ? 'negative' : ''}>$ {formatNumberToString(desfasesTotal)}</span>
                    </div>
                )}
                {advances.length > 0 && (
                    <div>
                        Anticipos sin descontar
                        <span className="negative">-$ {formatNumberToString(advancesTotal)}</span>
                    </div>
                )}
                <div>
                    Total a pagar
                    <span>$ {formatNumberToString(totalAPagar)}</span>
                </div>
            </div>
            {totalAPagar > 0 && (
                <div className="create-pending">
                    <button type="button" onClick={createWeeklyPendingPayment} disabled={!commissionCategoryId}>
                        Crear pago pendiente en Caja por ${formatNumberToString(totalAPagar)}
                    </button>
                    <button type="button" className="secondary" onClick={createWeeklySummary}>
                        Crear resumen (PDF)
                    </button>
                    <p className="hint">Los abonos y la asignación de movimientos a este pago se manejan desde Caja.</p>
                </div>
            )}
            {checkpoints.length > 0 && (
                <div className="checkpoints">
                    <h3>Desfases sin saldar</h3>
                    {checkpoints.map(c => (
                        <div className="row" key={c.id}>
                            <span>{c.shift_date} — {c.period} {c.checkpoint_type}: {c.difference > 0 ? '+' : ''}${formatNumberToString(c.difference)}</span>
                        </div>
                    ))}
                    <button type="button" onClick={settleDesfases}>Marcar como saldados</button>
                </div>
            )}
            {advances.length > 0 && (
                <div className="checkpoints">
                    <h3>Anticipos sin descontar</h3>
                    {advances.map(a => (
                        <div className="row" key={a.id}>
                            <span>{format(new Date(a.created_at), 'dd/MM/yyyy')} — ${formatNumberToString(Number(a.amount))}{a.detail ? ` — ${a.detail}` : ''}</span>
                        </div>
                    ))}
                    <p className="hint">Se descuentan solos al crear el próximo pago pendiente — no hace falta marcarlos aparte.</p>
                </div>
            )}
            <DaysWorked shifts={shifts} setShifts={setShifts} sellerName={sellerName} />
            {
                !data.sales?.length ?
                <h1>No hay ventas</h1>
                :
                <Sales
                sales={data.sales}
                setData={setData}
                />
            }
        </Main>
    )
}

const Main = styled.div`
display: flex;
justify-content: center;
align-items: center;
gap: 15px;
flex-direction: column;
margin-bottom: 20px;
.accounting {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-evenly;
    gap: 15px;
    div span {
        margin-left: 10px;
        background-color: ${props => props.theme.blue};
        color: ${props => props.theme.white};
        padding: 5px 10px;
    }
    div span.negative {
        background-color: ${props => props.theme.lightRed};
    }
}
.checkpoints {
    width: 100%;
    h3 {
        font-size: 15px;
        margin-bottom: 8px;
        text-align: center;
    }
    .row {
        padding: 6px 12px;
        border-radius: 8px;
        background: ${props => props.theme.whiteBackground};
        margin-bottom: 6px;
        font-size: 13px;
        text-align: center;
    }
    button {
        display: block;
        margin: 10px auto 0;
        padding: 6px 12px;
        border-radius: 6px;
        border: 2px solid ${props => props.theme.blue};
        background: transparent;
        color: ${props => props.theme.blue};
        cursor: pointer;
    }
    .hint {
        font-size: 12px;
        color: ${props => props.theme.gray};
        text-align: center;
        margin-top: 8px;
    }
}
.create-pending {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    button {
        padding: 10px 16px;
        border-radius: 8px;
        border: none;
        background-color: ${props => props.theme.blue};
        color: ${props => props.theme.white};
        font-weight: 600;
        cursor: pointer;
        &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }
        &.secondary {
            background-color: transparent;
            color: ${props => props.theme.blue};
            border: 1px solid ${props => props.theme.blue};
        }
    }
    .hint {
        font-size: 12px;
        color: ${props => props.theme.gray};
        text-align: center;
    }
}
`